import {
    generateClient,
    GraphQLQuery,
} from 'aws-amplify/api';
import { createDomainName } from '../../domain-names/services/mutations';
import { listDomainNames } from '../../domain-names/services/queries';

import {
    showErrorNotifications,
    showNetworkErrorNotification,
    showImportProgressNotification,
    showPartialImportFailureNotification
} from "../../../components/ServiceNotifications";
import { DomainName } from "../../domain-names/types";
import { TopLevelDomain } from "../../top-level-domains/types";
import { ParsedDomainData } from "./FileProcessingService";
import { TLDService } from '../../domain-names/services/TLDService';
import { DomainNormalizationService, SkipReasonService } from '../../../services/shared';

// Types for bulk import operations
export interface ImportResult {
    successful: number;
    failed: number;
    skipped: number;
    errors: ImportError[];
    processedDomains: string[];
    skipDetails: SkipDetail[];
    skipReasons: SkipReasonSummary;
}

export interface SkipDetail {
    domainName: string;
    normalizedName: string;
    rowNumber: number;
    skipReason: string;
    skipCategory: 'existing_in_system' | 'duplicate_in_file' | 'validation_error' | 'other';
    existingDomainId?: string;
}

export interface SkipReasonSummary {
    existingInSystem: number;
    duplicateInFile: number;
    validationError: number;
    other: number;
}

export interface ImportError {
    domainName: string;
    error: string;
    rowNumber: number;
    errorType?: 'validation' | 'duplicate' | 'creation' | 'network' | 'skip';
    normalizedName?: string;
    skipCategory?: 'existing_in_system' | 'duplicate_in_file' | 'other';
    existingDomainId?: string;
}

export interface ImportProgress {
    total: number;
    processed: number;
    successful: number;
    failed: number;
    currentDomain?: string;
    isComplete: boolean;
}

export interface BulkImportOptions {
    defaultRegistrarId: string;
    skipDuplicates: boolean;
    updateExisting: boolean;
    userId?: string;
}

export interface DuplicateCheckResult {
    existingDomains: Map<string, DomainName>;
    fileDuplicates: Map<string, number[]>;
    conflicts: DuplicateConflict[];
    normalizedDomains: Map<string, string>; // Original -> Normalized mapping
}

export interface DuplicateConflict {
    domainName: string;
    normalizedName: string; // Normalized domain name for clearer tracking
    rowNumber: number;
    existsInSystem: boolean;
    existsInFile: boolean;
    action: 'skip' | 'update' | 'create';
    existingDomain?: DomainName;
    skipReason?: string; // User-friendly reason for skipping the domain
}

export interface ImportValidationResult {
    canCreate: boolean;
    skipReason?: string;
    existingDomain?: DomainName;
}

// GraphQL types
export type CreateDomainName = {
    createDomainName: DomainName
}

export type ListDomainNames = {
    listDomainNames: { items: DomainName[] }
}

// Client will be generated when needed to allow for proper mocking
const getClient = () => generateClient();

/**
 * Service for handling bulk import operations of domain names.
 * 
 * Provides comprehensive functionality for:
 * - Batch processing of domain data
 * - Duplicate detection and resolution
 * - Progress tracking and error handling
 * - Integration with domain management system
 * 
 * @example
 * ```typescript
 * const result = await bulkImportService.importDomainNames(
 *   parsedData,
 *   { skipDuplicates: true, updateExisting: false },
 *   (progress) => console.log(`Progress: ${progress.percentage}%`)
 * );
 * ```
 */
export class BulkImportService {
    private readonly BATCH_SIZE = 5; // Process domains in batches to avoid overwhelming the API

    /**
     * Import domain names in bulk with enhanced duplicate prevention.
     * 
     * @param data - Array of parsed domain data to import
     * @param options - Import configuration options
     * @param onProgress - Optional callback for progress updates
     * @returns Promise resolving to import results with success/failure counts
     * 
     * @example
     * ```typescript
     * const result = await bulkImportService.importDomainNames(
     *   domains,
     *   { skipDuplicates: true, updateExisting: false },
     *   (progress) => updateUI(progress)
     * );
     * console.log(`Imported ${result.successful} domains`);
     * ```
     */
    async importDomainNames(
        data: ParsedDomainData[],
        options: BulkImportOptions,
        onProgress?: (progress: ImportProgress) => void
    ): Promise<ImportResult> {
        const result: ImportResult = {
            successful: 0,
            failed: 0,
            skipped: 0,
            errors: [],
            processedDomains: [],
            skipDetails: [],
            skipReasons: {
                existingInSystem: 0,
                duplicateInFile: 0,
                validationError: 0,
                other: 0
            }
        };

        const total = data.length;
        let processed = 0;

        // Enhanced logging for debugging duplicate detection issues
        console.log(`Starting bulk import of ${total} domains with options:`, {
            skipDuplicates: options.skipDuplicates,
            updateExisting: options.updateExisting
        });

        try {
            // Always perform comprehensive duplicate detection for enhanced safety
            const duplicateCheckResult = await this.detectDuplicates(data);

            // Log duplicate detection results for debugging
            console.log(`Duplicate detection completed:`, {
                existingDomainsCount: duplicateCheckResult.existingDomains.size,
                fileDuplicatesCount: duplicateCheckResult.fileDuplicates.size,
                conflictsCount: duplicateCheckResult.conflicts.length
            });

            // Create a map of domains that exist in system for quick lookup
            const existingInSystemMap = new Map<string, DomainName>();
            duplicateCheckResult.existingDomains.forEach((domain, normalizedName) => {
                existingInSystemMap.set(normalizedName, domain);
            });

            // Process domains in batches
            for (let i = 0; i < data.length; i += this.BATCH_SIZE) {
                const batch = data.slice(i, i + this.BATCH_SIZE);

                // Process batch concurrently
                const batchPromises = batch.map(async (domainData) => {
                    const normalizedDomain = DomainNormalizationService.normalizeDomainName(domainData.domainName);

                    // Update progress
                    if (onProgress) {
                        onProgress({
                            total,
                            processed,
                            successful: result.successful,
                            failed: result.failed,
                            currentDomain: domainData.domainName,
                            isComplete: false
                        });
                    }

                    try {
                        // CRITICAL: Ensure domains marked as existing in system are NEVER created
                        // regardless of user choices or settings
                        if (existingInSystemMap.has(normalizedDomain)) {
                            const existingDomain = existingInSystemMap.get(normalizedDomain);
                            const skipReason = `Domain already exists in your account`;

                            // Log for debugging
                            console.log(`Skipping existing domain: ${domainData.domainName} -> ${normalizedDomain} - ${skipReason}`);

                            // Enhanced skip tracking with detailed information
                            result.skipped++;
                            result.skipReasons.existingInSystem++;
                            result.processedDomains.push(domainData.domainName);

                            // Add to skip details for comprehensive reporting
                            result.skipDetails.push({
                                domainName: domainData.domainName,
                                normalizedName: normalizedDomain,
                                rowNumber: domainData.rowNumber,
                                skipReason: skipReason,
                                skipCategory: 'existing_in_system',
                                existingDomainId: existingDomain?.id
                            });

                            // Consistent error handling for domains skipped due to duplicates
                            result.errors.push({
                                domainName: domainData.domainName,
                                error: skipReason,
                                rowNumber: domainData.rowNumber,
                                errorType: 'duplicate',
                                normalizedName: normalizedDomain,
                                skipCategory: 'existing_in_system',
                                existingDomainId: existingDomain?.id
                            });
                            return;
                        }

                        // Extract and ensure TLD exists
                        const tld = await this.extractAndEnsureTLD(domainData.domainName);
                        if (!tld) {
                            throw new Error(`Invalid or unsupported TLD for domain: ${domainData.domainName}`);
                        }

                        // Create domain name record with enhanced safety check
                        const domainInput = this.mapToDomainInput(domainData, options, tld.id);
                        const createdDomain = await this.createSingleDomainWithEnhancedSafetyCheck(
                            domainInput,
                            normalizedDomain,
                            domainData.rowNumber
                        );

                        if (createdDomain) {
                            result.successful++;
                            result.processedDomains.push(domainData.domainName);
                            console.log(`Successfully created domain: ${domainData.domainName} -> ${normalizedDomain}`);
                        } else {
                            // Domain was skipped by safety check - treat as skip with proper error tracking
                            const skipReason = 'Domain creation skipped by final safety check';
                            result.skipped++;
                            result.skipReasons.other++;
                            result.processedDomains.push(domainData.domainName);

                            // Add to skip details for comprehensive reporting
                            result.skipDetails.push({
                                domainName: domainData.domainName,
                                normalizedName: normalizedDomain,
                                rowNumber: domainData.rowNumber,
                                skipReason: skipReason,
                                skipCategory: 'other'
                            });

                            // Add to errors for consistent reporting
                            result.errors.push({
                                domainName: domainData.domainName,
                                error: skipReason,
                                rowNumber: domainData.rowNumber,
                                errorType: 'skip',
                                normalizedName: normalizedDomain,
                                skipCategory: 'other'
                            });

                            console.log(`Domain creation skipped by safety check: ${domainData.domainName} -> ${normalizedDomain}`);
                        }

                    } catch (error) {
                        result.failed++;
                        const errorMessage = this.getDetailedErrorMessage(error, domainData.domainName);
                        const errorType = this.determineErrorType(error);

                        // Determine skip category for validation errors that should be treated as skips
                        let skipCategory: 'existing_in_system' | 'duplicate_in_file' | 'other' | undefined;
                        if (errorType === 'duplicate') {
                            skipCategory = 'existing_in_system';
                            result.skipReasons.existingInSystem++;
                        }

                        result.errors.push({
                            domainName: domainData.domainName,
                            error: errorMessage,
                            rowNumber: domainData.rowNumber,
                            errorType: errorType,
                            normalizedName: normalizedDomain,
                            skipCategory: skipCategory
                        });

                        // Enhanced logging for debugging duplicate detection issues
                        console.error(`Failed to import domain ${domainData.domainName} -> ${normalizedDomain}:`, {
                            error: error,
                            errorMessage: errorMessage,
                            errorType: errorType,
                            rowNumber: domainData.rowNumber,
                            normalizedDomain: normalizedDomain,
                            skipCategory: skipCategory
                        });
                    }
                });

                // Wait for batch to complete
                await Promise.all(batchPromises);
                processed += batch.length;

                // Update progress after batch completion
                if (onProgress) {
                    onProgress({
                        total,
                        processed,
                        successful: result.successful,
                        failed: result.failed,
                        currentDomain: undefined,
                        isComplete: processed >= total
                    });
                }
            }

            // Enhanced logging for final results
            console.log(`Bulk import completed:`, {
                total: total,
                successful: result.successful,
                failed: result.failed,
                skipped: result.skipped,
                errorsCount: result.errors.length
            });

            // Show final notification with enhanced feedback
            this.showImportCompletionNotification(result);

            return result;

        } catch (error) {
            console.error('Bulk import failed with error:', error);
            
            // For GraphQL errors during duplicate detection, return failed result instead of throwing
            if (error instanceof Error && error.message.includes('GraphQL query failed')) {
                result.failed = data.length; // Mark all as failed
                result.errors = data.map(item => ({
                    domainName: item.domainName,
                    error: error.message,
                    rowNumber: item.rowNumber,
                    errorType: 'network' as const
                }));
                return result;
            }
            
            this.handleImportError(error);
            throw error;
        }
    }

    /**
     * Check for existing domain names
     * 
     * Note: Backend automatically filters results to current user's domains
     * @param domainNames - Array of domain names to check
     * @param userId - DEPRECATED: This parameter is no longer used. Backend handles user isolation automatically.
     */
    async checkForDuplicates(domainNames: string[], userId?: string): Promise<string[]> {
        // Show deprecation warning if userId is provided
        if (userId !== undefined) {
            console.warn('checkForDuplicates: userId parameter is deprecated and no longer used. Backend now handles user isolation filtering automatically.');
        }
        try {
            return await this.getExistingDomainNames(domainNames);
        } catch (error) {
            console.error('Error checking for duplicates:', error);
            showErrorNotifications(error);
            return [];
        }
    }

    /**
     * Comprehensive duplicate detection
     * 
     * Note: User isolation is handled automatically by the backend GraphQL resolvers
     * based on the authenticated user's context. No client-side filtering needed.
     * @param data - Array of parsed domain data
     * @param userId - DEPRECATED: This parameter is no longer used. Backend handles user isolation automatically.
     */
    async detectDuplicates(data: ParsedDomainData[], userId?: string): Promise<DuplicateCheckResult> {
        // Show deprecation warning if userId is provided
        if (userId !== undefined) {
            console.warn('detectDuplicates: userId parameter is deprecated and no longer used. Backend now handles user isolation filtering automatically.');
        }
        try {
            const result: DuplicateCheckResult = {
                existingDomains: new Map(),
                fileDuplicates: new Map(),
                conflicts: [],
                normalizedDomains: new Map()
            };

            // Step 1: Create normalized domain mapping using DomainNormalizationService
            const originalDomains = data.map(item => item.domainName);
            result.normalizedDomains = DomainNormalizationService.createNormalizedMap(originalDomains);

            // Step 2: Detect duplicates within the file using normalized names
            const domainCounts = new Map<string, number[]>();
            data.forEach((item) => {
                const normalizedDomain = DomainNormalizationService.normalizeDomainName(item.domainName);
                if (!domainCounts.has(normalizedDomain)) {
                    domainCounts.set(normalizedDomain, []);
                }
                domainCounts.get(normalizedDomain)!.push(item.rowNumber);
            });

            // Identify file duplicates
            domainCounts.forEach((rowNumbers, domainName) => {
                if (rowNumbers.length > 1) {
                    result.fileDuplicates.set(domainName, rowNumbers);
                }
            });

            // Step 3: Check against existing domains (backend automatically filters by current user)
            const allNormalizedDomainNames = Array.from(domainCounts.keys());
            const existingDomains = await this.getExistingDomainsDetailed(allNormalizedDomainNames);

            existingDomains.forEach(domain => {
                if (domain.name) {
                    const normalizedName = DomainNormalizationService.normalizeDomainName(domain.name);
                    result.existingDomains.set(normalizedName, domain);
                }
            });

            // Step 4: Create conflict list with normalized domain tracking
            data.forEach(item => {
                const normalizedDomain = DomainNormalizationService.normalizeDomainName(item.domainName);
                const existsInSystem = result.existingDomains.has(normalizedDomain);
                const existsInFile = result.fileDuplicates.has(normalizedDomain);

                if (existsInSystem || existsInFile) {
                    // Determine default action
                    let defaultAction: 'skip' | 'update' | 'create' = 'skip';
                    if (existsInSystem && !existsInFile) {
                        defaultAction = 'skip'; // Default to skip existing domains
                    } else if (existsInFile && !existsInSystem) {
                        // For file duplicates, only create the first occurrence
                        const rowNumbers = result.fileDuplicates.get(normalizedDomain) || [];
                        defaultAction = item.rowNumber === Math.min(...rowNumbers) ? 'create' : 'skip';
                    } else if (existsInSystem && existsInFile) {
                        defaultAction = 'skip'; // Default to skip when both conditions exist
                    }

                    const conflict: DuplicateConflict = {
                        domainName: item.domainName,
                        normalizedName: normalizedDomain,
                        rowNumber: item.rowNumber,
                        existsInSystem,
                        existsInFile,
                        action: defaultAction,
                        existingDomain: result.existingDomains.get(normalizedDomain)
                    };

                    // Generate user-friendly skip reason
                    conflict.skipReason = SkipReasonService.generateSkipReason(conflict);

                    result.conflicts.push(conflict);
                }
            });

            return result;

        } catch (error) {
            console.error('CRITICAL ERROR: Duplicate detection failed - this could allow duplicate creation!', {
                error,
                domainCount: data.length,
                errorMessage: error instanceof Error ? error.message : 'Unknown error'
            });
            showErrorNotifications(error);
            throw error;
        }
    }

    /**
     * Perform final duplicate check before domain creation
     * This is a safety mechanism to prevent any duplicate creation
     * @param domainName - Domain name to check
     * @param userId - DEPRECATED: This parameter is no longer used. Backend handles user isolation automatically.
     */
    async performFinalDuplicateCheck(domainName: string, userId?: string): Promise<ImportValidationResult> {
        // Show deprecation warning if userId is provided
        if (userId !== undefined) {
            console.warn('performFinalDuplicateCheck: userId parameter is deprecated and no longer used. Backend now handles user isolation filtering automatically.');
        }
        try {
            const normalizedDomain = DomainNormalizationService.normalizeDomainName(domainName);

            // Query backend for existing domain using normalized name
            // Note: For backward compatibility with tests, we still pass the userId parameter even though it's deprecated
            const existingDomains = await this.getExistingDomainsDetailed([normalizedDomain], userId);

            if (existingDomains.length > 0) {
                const existingDomain = existingDomains[0];
                return {
                    canCreate: false,
                    skipReason: 'Domain already exists in your account',
                    existingDomain: existingDomain
                };
            }

            return {
                canCreate: true
            };

        } catch (error) {
            console.error(`Error performing final duplicate check for domain ${domainName}:`, error);
            // In case of error, err on the side of caution and prevent creation
            return {
                canCreate: false,
                skipReason: 'Unable to verify domain uniqueness due to system error'
            };
        }
    }

    /**
     * Generate skip details from duplicate conflicts for accurate reporting
     */
    generateSkipDetailsFromConflicts(conflicts: DuplicateConflict[]): SkipDetail[] {
        return conflicts
            .filter(conflict => conflict.action === 'skip')
            .map(conflict => {
                let skipCategory: 'existing_in_system' | 'duplicate_in_file' | 'other';
                let skipReason: string;

                if (conflict.existsInSystem && conflict.existsInFile) {
                    skipCategory = 'existing_in_system';
                    skipReason = 'Domain exists in your account and appears multiple times in the file';
                } else if (conflict.existsInSystem) {
                    skipCategory = 'existing_in_system';
                    skipReason = 'Domain already exists in your account';
                } else if (conflict.existsInFile) {
                    skipCategory = 'duplicate_in_file';
                    skipReason = 'Duplicate domain in uploaded file (keeping first occurrence)';
                } else {
                    skipCategory = 'other';
                    skipReason = conflict.skipReason || 'Unknown duplicate conflict';
                }

                return {
                    domainName: conflict.domainName,
                    normalizedName: conflict.normalizedName,
                    rowNumber: conflict.rowNumber,
                    skipReason: skipReason,
                    skipCategory: skipCategory,
                    existingDomainId: conflict.existingDomain?.id
                };
            });
    }

    /**
     * Calculate skip reason summary from skip details
     */
    calculateSkipReasonSummary(skipDetails: SkipDetail[]): SkipReasonSummary {
        const summary: SkipReasonSummary = {
            existingInSystem: 0,
            duplicateInFile: 0,
            validationError: 0,
            other: 0
        };

        skipDetails.forEach(detail => {
            switch (detail.skipCategory) {
                case 'existing_in_system':
                    summary.existingInSystem++;
                    break;
                case 'duplicate_in_file':
                    summary.duplicateInFile++;
                    break;
                case 'validation_error':
                    summary.validationError++;
                    break;
                case 'other':
                    summary.other++;
                    break;
            }
        });

        return summary;
    }

    /**
     * Apply duplicate resolution actions to filter data for import
     * CRITICAL: Domains marked as existing in system are NEVER included in toImport
     */
    applyDuplicateResolution(
        data: ParsedDomainData[],
        conflicts: DuplicateConflict[]
    ): { toImport: ParsedDomainData[], toUpdate: ParsedDomainData[], toSkip: ParsedDomainData[] } {
        const result = {
            toImport: [] as ParsedDomainData[],
            toUpdate: [] as ParsedDomainData[],
            toSkip: [] as ParsedDomainData[]
        };

        // Create a map of conflicts for quick lookup using normalized names
        const conflictMap = new Map<string, DuplicateConflict>();
        conflicts.forEach(conflict => {
            const key = `${conflict.normalizedName}-${conflict.rowNumber}`;
            conflictMap.set(key, conflict);
        });

        // Create a map to track file duplicates and ensure only first occurrence is considered for creation
        const fileDuplicateTracker = new Map<string, number>();
        conflicts.forEach(conflict => {
            if (conflict.existsInFile && !conflict.existsInSystem) {
                const existing = fileDuplicateTracker.get(conflict.normalizedName);
                if (!existing || conflict.rowNumber < existing) {
                    fileDuplicateTracker.set(conflict.normalizedName, conflict.rowNumber);
                }
            }
        });

        data.forEach(item => {
            const normalizedDomain = DomainNormalizationService.normalizeDomainName(item.domainName);
            const key = `${normalizedDomain}-${item.rowNumber}`;
            const conflict = conflictMap.get(key);

            if (conflict) {
                // CRITICAL SAFEGUARD: Domains marked as existing in system are NEVER created
                if (conflict.existsInSystem && conflict.action === 'create') {
                    // Force skip action for domains that exist in system when user tries to create
                    result.toSkip.push(item);
                    console.log(`Safeguard: Domain ${item.domainName} exists in system - forcing skip for create action`);
                    return;
                }

                // Handle file duplicates - only first occurrence can be created
                if (conflict.existsInFile && !conflict.existsInSystem) {
                    const firstOccurrenceRow = fileDuplicateTracker.get(normalizedDomain);
                    if (item.rowNumber !== firstOccurrenceRow) {
                        // Not the first occurrence of this file duplicate - force skip
                        result.toSkip.push(item);
                        console.log(`File duplicate: Domain ${item.domainName} at row ${item.rowNumber} is not first occurrence (first at row ${firstOccurrenceRow}) - forcing skip`);
                        return;
                    }
                }

                // Apply user resolution choice for non-system duplicates
                switch (conflict.action) {
                    case 'create':
                        // Additional validation: only allow create if domain doesn't exist in system
                        if (!conflict.existsInSystem) {
                            result.toImport.push(item);
                        } else {
                            // This should never happen due to the safeguard above, but adding for extra safety
                            result.toSkip.push(item);
                            console.warn(`Bypass attempt detected: Domain ${item.domainName} exists in system but was marked for creation - forcing skip`);
                        }
                        break;
                    case 'update':
                        result.toUpdate.push(item);
                        break;
                    case 'skip':
                    default:
                        result.toSkip.push(item);
                        break;
                }
            } else {
                // No conflict, safe to import
                result.toImport.push(item);
            }
        });

        // Log resolution results for debugging
        console.log(`Duplicate resolution applied:`, {
            totalDomains: data.length,
            toImport: result.toImport.length,
            toUpdate: result.toUpdate.length,
            toSkip: result.toSkip.length,
            conflicts: conflicts.length
        });

        return result;
    }

    /**
     * Extract TLD from domain name and ensure it exists in the system
     */
    async extractAndEnsureTLD(domainName: string): Promise<TopLevelDomain | null> {
        try {
            return await TLDService.extractAndEnsureTLD(domainName);
        } catch (error) {
            console.error(`Error processing TLD for domain ${domainName}:`, error);
            throw error;
        }
    }

    /**
     * Get existing domain names
     * 
     * Note: Backend automatically filters results to current user's domains
     */
    private async getExistingDomainNames(domainNames: string[]): Promise<string[]> {
        try {
            const normalizedDomains = domainNames.map(name => DomainNormalizationService.normalizeDomainName(name));
            const existingDomains = await this.getExistingDomainsDetailed(normalizedDomains);
            return existingDomains.map(d => d.name ? DomainNormalizationService.normalizeDomainName(d.name) : '').filter((name): name is string => Boolean(name));
        } catch (error) {
            console.error('Error fetching existing domain names:', error);
            return [];
        }
    }

    /**
     * Get detailed existing domain records
     * 
     * Note: Backend GraphQL resolvers automatically handle user isolation filtering
     * based on the authenticated user's context. No client-side filtering needed.
     * @param domainNames - Array of domain names to check
     * @param userId - DEPRECATED: This parameter is no longer used. Backend handles user isolation automatically.
     */
    private async getExistingDomainsDetailed(domainNames: string[], userId?: string): Promise<DomainName[]> {
        // Show deprecation warning if userId is provided
        if (userId !== undefined) {
            console.warn('getExistingDomainsDetailed: userId parameter is deprecated and no longer used. Backend now handles user isolation filtering automatically.');
        }
        try {
            if (domainNames.length === 0) {
                return [];
            }

            // Create filter for domain names using consistent normalization - backend handles user isolation automatically
            const normalizedNames = domainNames.map(name => DomainNormalizationService.normalizeDomainName(name));

            console.log(`Querying for existing domains:`, {
                domainCount: domainNames.length,
                domains: domainNames,
                normalizedNames
            });

            // Create specific filter for the domain names we're looking for
            // Backend handles user isolation automatically
            const filter = {
                or: normalizedNames.map(name => ({ name: { eq: name } }))
            };

            const matchingDomains = await this.fetchDomainNames(filter);

            if (!matchingDomains) {
                return [];
            }

            console.log(`Found existing domains:`, {
                count: matchingDomains.length,
                domains: matchingDomains.map(d => ({ id: d.id, name: d.name, createdBy: d.createdByUsername }))
            });

            return matchingDomains;

        } catch (error) {
            console.error('CRITICAL ERROR: Failed to fetch existing domain details - this will cause duplicates to be created!', {
                error,
                domainNames: domainNames.slice(0, 5), // Show first 5 domains for debugging
                domainCount: domainNames.length,
                errorMessage: error instanceof Error ? error.message : 'Unknown error',
                errorStack: error instanceof Error ? error.stack : undefined,
                errorName: error instanceof Error ? error.name : undefined
            });

            // Instead of silently returning empty array, throw the error to prevent duplicate creation
            throw new Error(`Failed to verify existing domains: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }

    /**
     * Fetch domain names with filter
     */
    private async fetchDomainNames(filter: object): Promise<DomainName[] | undefined> {
        try {
            console.log('Executing GraphQL query for domain names:', {
                query: 'listDomainNames',
                filter
            });

            const response = await getClient().graphql<GraphQLQuery<ListDomainNames>>({
                query: listDomainNames,
                variables: { filter: filter },
            });

            console.log('GraphQL query response:', {
                success: !!response.data,
                itemCount: response.data?.listDomainNames.items?.length || 0,
                hasErrors: !!response.errors,
                errors: response.errors
            });

            if (response.errors && response.errors.length > 0) {
                throw new Error(`GraphQL errors: ${response.errors.map(e => e.message).join(', ')}`);
            }

            return response.data?.listDomainNames.items;
        } catch (error) {
            console.error('GraphQL query failed:', {
                error,
                filter,
                errorMessage: error instanceof Error ? error.message : 'Unknown error',
                errorStack: error instanceof Error ? error.stack : undefined,
                errorName: error instanceof Error ? error.name : undefined,
                // Additional GraphQL error details
                graphqlErrors: (error as any)?.errors || undefined,
                networkError: (error as any)?.networkError || undefined,
                // AWS AppSync specific error details
                data: (error as any)?.data || undefined,
                extensions: (error as any)?.extensions || undefined
            });

            // Try to extract more meaningful error information
            let detailedError = 'Unknown GraphQL error';
            if ((error as any)?.errors && Array.isArray((error as any).errors)) {
                detailedError = (error as any).errors.map((e: any) => e.message || e.errorType || 'Unknown').join(', ');
            } else if (error instanceof Error) {
                detailedError = error.message;
            }

            throw new Error(`GraphQL query failed: ${detailedError}`);
        }
    }

    /**
     * Create a single domain name record with enhanced safety check
     */
    private async createSingleDomainWithEnhancedSafetyCheck(
        domainInput: any,
        normalizedDomain: string,
        rowNumber: number
    ): Promise<DomainName | null> {
        try {
            // Perform final safety check before creation using normalized domain
            const safetyCheck = await this.performFinalDuplicateCheck(normalizedDomain);

            if (!safetyCheck.canCreate) {
                // Enhanced logging for debugging duplicate detection issues
                console.log(`Final safety check prevented domain creation:`, {
                    originalName: domainInput.name,
                    normalizedName: normalizedDomain,
                    rowNumber: rowNumber,
                    skipReason: safetyCheck.skipReason,
                    existingDomainId: safetyCheck.existingDomain?.id
                });
                return null;
            }

            // Log successful safety check pass
            console.log(`Final safety check passed for domain: ${domainInput.name} -> ${normalizedDomain}`);

            // Proceed with creation if safety check passes
            return await this.createSingleDomain(domainInput);

        } catch (error) {
            // Enhanced error logging for debugging
            console.error(`Error in enhanced safety check for domain:`, {
                originalName: domainInput.name,
                normalizedName: normalizedDomain,
                rowNumber: rowNumber,
                error: error
            });
            throw error;
        }
    }

    /**
     * Create a single domain name record
     */
    private async createSingleDomain(domainInput: any): Promise<DomainName | null> {
        try {
            const response = await getClient().graphql<GraphQLQuery<CreateDomainName>>({
                query: createDomainName,
                variables: { input: domainInput },
            });

            return response.data?.createDomainName || null;
        } catch (error) {
            console.error('Error creating domain:', error);
            throw error;
        }
    }

    /**
     * Show import completion notification with detailed feedback
     */
    private showImportCompletionNotification(result: ImportResult): void {
        if (result.successful === 0 && result.failed > 0) {
            // Complete failure
            showErrorNotifications(new Error(`Import failed: ${result.failed} domains could not be imported. Check the error report for details.`));
        } else if (result.failed > 0) {
            // Partial failure
            showPartialImportFailureNotification(result.successful, result.failed, result.errors.length);
        } else {
            // Complete success
            showImportProgressNotification(result.successful, result.failed, result.skipped);
        }
    }

    /**
     * Handle import errors with specific error types
     */
    private handleImportError(error: any): void {
        if (this.isNetworkError(error)) {
            showNetworkErrorNotification('Import failed due to network issues. Please check your connection and try again.');
        } else if (this.isAuthenticationError(error)) {
            showErrorNotifications(new Error('Authentication failed. Please sign in again and retry the import.'));
        } else if (this.isPermissionError(error)) {
            showErrorNotifications(new Error('Permission denied. You do not have permission to import domain names.'));
        } else {
            showErrorNotifications(error);
        }
    }

    /**
     * Get detailed error message for specific domain import failures
     */
    private getDetailedErrorMessage(error: any, _domainName: string): string {
        if (this.isNetworkError(error)) {
            return 'Network error - please check your connection';
        }

        if (this.isValidationError(error)) {
            return this.extractValidationErrorMessage(error);
        }

        if (this.isDuplicateError(error)) {
            return 'Domain name already exists in the system';
        }

        if (this.isPermissionError(error)) {
            return 'Permission denied - insufficient privileges';
        }

        if (this.isAuthenticationError(error)) {
            return 'Authentication failed - please sign in again';
        }

        // GraphQL specific errors
        if (error?.errors && Array.isArray(error.errors)) {
            return error.errors.map((e: any) => e.message || 'GraphQL error').join('; ');
        }

        // Generic error fallback
        return error instanceof Error ? error.message : 'Unknown error occurred';
    }

    /**
     * Check if error is a network-related error
     */
    private isNetworkError(error: any): boolean {
        const networkErrorIndicators = [
            'network error',
            'fetch failed',
            'connection refused',
            'timeout',
            'network request failed',
            'failed to fetch'
        ];

        const errorMessage = (error?.message || '').toLowerCase();
        return networkErrorIndicators.some(indicator => errorMessage.includes(indicator));
    }

    /**
     * Check if error is an authentication error
     */
    private isAuthenticationError(error: any): boolean {
        const authErrorIndicators = [
            'unauthorized',
            'authentication',
            'no current user',
            'no federated jwt',
            'token expired',
            'invalid token'
        ];

        const errorMessage = (error?.message || '').toLowerCase();
        return authErrorIndicators.some(indicator => errorMessage.includes(indicator));
    }

    /**
     * Check if error is a permission error
     */
    private isPermissionError(error: any): boolean {
        const permissionErrorIndicators = [
            'access denied',
            'forbidden',
            'permission denied',
            'not authorized',
            'insufficient privileges'
        ];

        const errorMessage = (error?.message || '').toLowerCase();
        return permissionErrorIndicators.some(indicator => errorMessage.includes(indicator));
    }

    /**
     * Check if error is a validation error
     */
    private isValidationError(error: any): boolean {
        const validationErrorIndicators = [
            'validation',
            'invalid input',
            'required field',
            'format error',
            'constraint violation'
        ];

        const errorMessage = (error?.message || '').toLowerCase();
        return validationErrorIndicators.some(indicator => errorMessage.includes(indicator));
    }

    /**
     * Check if error is a duplicate error
     */
    private isDuplicateError(error: any): boolean {
        const duplicateErrorIndicators = [
            'duplicate',
            'already exists',
            'unique constraint',
            'conflict'
        ];

        const errorMessage = (error?.message || '').toLowerCase();
        return duplicateErrorIndicators.some(indicator => errorMessage.includes(indicator));
    }

    /**
     * Extract meaningful validation error message
     */
    private extractValidationErrorMessage(error: any): string {
        if (error?.errors && Array.isArray(error.errors)) {
            const validationErrors = error.errors
                .filter((e: any) => e.message && e.message.toLowerCase().includes('validation'))
                .map((e: any) => e.message);

            if (validationErrors.length > 0) {
                return validationErrors.join('; ');
            }
        }

        return error?.message || 'Validation failed';
    }

    /**
     * Determine the error type for consistent categorization
     */
    private determineErrorType(error: any): 'validation' | 'duplicate' | 'creation' | 'network' | 'skip' {
        if (this.isNetworkError(error)) {
            return 'network';
        }

        if (this.isValidationError(error)) {
            return 'validation';
        }

        if (this.isDuplicateError(error)) {
            return 'duplicate';
        }

        // Default to creation error for other types
        return 'creation';
    }

    /**
     * Map parsed domain data to GraphQL input format
     */
    private mapToDomainInput(
        domainData: ParsedDomainData,
        options: BulkImportOptions,
        tldId: string
    ): any {
        return {
            name: DomainNormalizationService.normalizeDomainName(domainData.domainName),
            tldId: tldId,
            registrarId: options.defaultRegistrarId,
            expirationDate: domainData.expirationDate ? new Date(domainData.expirationDate).toISOString() : undefined,
            purchaseDate: domainData.ownershipDate ? new Date(domainData.ownershipDate).toISOString() : undefined,
            category: 'imported' // Default category for bulk imported domains
        };
    }
}

// Export singleton instance
export const bulkImportService = new BulkImportService();
export default bulkImportService;